import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const guides = [
  {
    id: "getting-started",
    title: "Getting Started with Home Care",
    description: "Everything families need to know before starting home care services — from assessments to first-day expectations.",
    icon: "ri-home-heart-line",
    items: [
      "Schedule a free in-home assessment",
      "Prepare a list of your loved one's daily routines",
      "Gather important contact information and preferences",
      "Identify priority needs (mobility, meals, companionship)",
      "Discuss boundaries and household preferences",
      "Set up a comfortable space for the caregiver",
      "Plan a family meeting to align on care goals",
    ],
  },
  {
    id: "caregiver-interview",
    title: "Caregiver Interview Guide",
    description: "Key questions to ask when meeting a potential caregiver to ensure the right fit for your family.",
    icon: "ri-user-search-line",
    items: [
      "What inspired you to become a caregiver?",
      "How do you handle difficult behaviors or confusion?",
      "Can you describe your experience with memory care?",
      "How do you communicate with family members about daily updates?",
      "What would you do in an emergency situation?",
      "How do you respect client privacy and dignity?",
      "What activities do you enjoy doing with clients?",
    ],
  },
  {
    id: "emergency-plan",
    title: "Emergency Contact & Plan Template",
    description: "A ready-to-fill template for keeping critical contacts, medications, and instructions in one place.",
    icon: "ri-first-aid-kit-line",
    items: [
      "Primary care physician name and phone",
      "List of all current medications with dosages",
      "Known allergies and reactions",
      "Emergency contact numbers for family members",
      "Preferred hospital and insurance details",
      "Important daily care instructions and routines",
      "Durable power of attorney for care decisions (if applicable)",
    ],
  },
  {
    id: "self-care",
    title: "Family Caregiver Self-Care Guide",
    description: "Practical reminders for family members balancing caregiving with their own wellbeing.",
    icon: "ri-mental-health-line",
    items: [
      "Accept help from others without guilt",
      "Schedule regular breaks and respite care",
      "Maintain your own appointments and wellbeing",
      "Join a local or online caregiver support group",
      "Set realistic expectations for yourself",
      "Keep a journal to process emotions",
      "Celebrate small wins and moments of joy",
    ],
  },
  {
    id: "transition-guide",
    title: "Hospital-to-Home Transition Guide",
    description: "Steps to ensure a smooth and safe transition when a loved one returns home from a stay.",
    icon: "ri-hospital-line",
    items: [
      "Review discharge instructions with the discharge coordinator",
      "Pick up all prescribed items before leaving",
      "Schedule follow-up appointments within one week",
      "Arrange home care before discharge day",
      "Set up needed equipment (bed rail, walker, commode)",
      "Create a daily schedule and reminder system",
      "Watch for warning signs and know when to call the doctor",
    ],
  },
];

export default function Resources() {
  const [isVisible, setIsVisible] = useState<Record<string, boolean>>({});
  const observerRefs = useRef<Map<string, HTMLElement>>(new Map());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute("data-animate-id");
          if (id && entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    observerRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const setRef = (id: string) => (el: HTMLElement | null) => {
    if (el) observerRefs.current.set(id, el);
  };

  const animateClass = (id: string, base = "") =>
    `transition-all duration-700 ease-out ${base} ${
      isVisible[id] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
    }`;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[300px] md:h-[380px] flex items-center justify-center overflow-hidden bg-brand-green-800">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20soft%20abstract%20green%20and%20cream%20background%20with%20gentle%20organic%20shapes%20and%20subtle%20texture%2C%20calming%20healthcare%20themed%20atmosphere%2C%20minimal%20elegant%20design%2C%20no%20text%2C%20professional%20and%20inviting&width=1600&height=450&seq=reshero&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-300 mb-3 block">
            Helpful Tools
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
            Family Guide & Resources
          </h1>
          <p className="text-sm md:text-base text-brand-green-200 max-w-2xl mx-auto leading-relaxed">
            Practical checklists, guides, and templates to help families prepare for and navigate home care with confidence.
          </p>
        </div>
      </section>

      {/* Guides Grid */}
      <section className="py-14 md:py-20 bg-white" data-animate-id="guides" ref={setRef("guides")}>
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className={animateClass("guides", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Free Downloads
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Downloadable Checklists & Guides
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              Print or save these resources to use during your home care journey. No sign-up required.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {guides.map((guide, i) => (
              <div
                key={guide.id}
                className={`group bg-white rounded-2xl border border-gray-100 hover:border-brand-green-200 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${
                  isVisible["guides"] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="p-6 md:p-7">
                  <div className="w-12 h-12 rounded-xl bg-brand-green-50 flex items-center justify-center mb-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className={`${guide.icon} text-xl text-brand-green-600`} />
                    </div>
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2">
                    {guide.title}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4 leading-relaxed">
                    {guide.description}
                  </p>

                  <div className="border-t border-gray-100 pt-4">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">
                      Checklist Items
                    </p>
                    <ul className="space-y-2.5">
                      {guide.items.map((item) => (
                        <li key={item} className="flex items-start gap-2.5">
                          <div className="w-4 h-4 rounded-sm border-2 border-brand-green-300 shrink-0 mt-0.5" />
                          <span className="text-xs text-gray-600 leading-snug">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Print Hint */}
      <section className="py-10 md:py-14 bg-soft-gray border-t border-gray-100">
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto text-center">
          <div className="w-12 h-12 rounded-full bg-brand-green-100 flex items-center justify-center mx-auto mb-4">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-printer-line text-xl text-brand-green-600" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Print Any Guide Instantly
          </h3>
          <p className="text-sm text-gray-500 max-w-xl mx-auto mb-6">
            Each checklist is formatted for easy printing. Simply open the guide you need and use your browser's print function to create a physical copy for your fridge, binder, or care folder.
          </p>
          <p className="text-xs text-gray-400">
            Tip: Use "Save as PDF" in your print dialog to keep a digital copy on your phone or tablet.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 bg-brand-green-800">
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto text-center">
          <div className="w-12 h-12 rounded-full bg-brand-green-700 flex items-center justify-center mx-auto mb-4">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-heart-3-line text-2xl text-brand-green-300" />
            </div>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Need Personalized Guidance?
          </h2>
          <p className="text-sm md:text-base text-brand-green-200 mb-8 max-w-2xl mx-auto leading-relaxed">
            Our care advisors are happy to walk you through any of these topics over the phone or during your free in-home assessment.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <Link
              to="/contact"
              className="w-full sm:w-auto px-8 py-3.5 bg-white text-brand-green-800 font-semibold rounded-full hover:bg-brand-green-50 transition-all duration-200 text-center whitespace-nowrap"
            >
              Request Free Assessment
            </Link>
            <Link
              to="/faq"
              className="w-full sm:w-auto px-8 py-3.5 bg-brand-green-700 text-white font-semibold rounded-full hover:bg-brand-green-600 transition-all duration-200 text-center whitespace-nowrap"
            >
              Visit FAQ
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}